// @flow
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Divider,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AssignmentIcon,
  ContractIcon,
  ChecklistIcon,
  PencilIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  green,
  yellow,
} from '@fortress-technology-solutions/fortress-component-library/design';
import { getUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import useIntl from '../../../hooks/useIntl';
import useHasPermission from '../../../hooks/useHasPermission';
import messages from './messages';
import {
  buildTransferActionLayout,
  TRANSFER_ACTION_ROW_SPACING,
} from '../transferActionLayout';
import {
  useActiveTransfer,
  useUpdateTransferActions,
  useCancelActiveTransfer,
  useCompleteActiveTransfer,
  useEditActiveTransfer,
} from './hooks';
import CancelTransferModal from './CancelTransferModal';
import CompleteTransferModal from './CompleteTransferModal';
import StartTransferModal from '../StartTransferModal';

const ACTION_KEYS = [
  'verifiedIncome',
  'proratedPaid',
  'collectedPriorUnitKeys',
  'pickedNewUnitKeys',
];

// Backend returns transferDate as a DATEONLY 'YYYY-MM-DD' string; display it as MM/DD/YYYY
// (per spec Sc5 / mockup) without constructing a Date, to avoid any timezone shift.
const formatTransferDate = (value: ?string): string => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${month}/${day}/${year}` : value;
};

type Props = {
  organizationId: string,
  propertyId: string,
  householdId: string,
  enabled: boolean,
  transferReasonOptions: Array<{ value: string, text: string }>,
  onTransferChanged?: () => void,
  // Refreshes the redux-backed resident profile (leases, transfer state) after a successful
  // cancel, so the Lease Data tab stops showing the now-removed transfer lease (ENG-30823).
  onCancelSuccess?: () => void,
  // Refreshes the redux-backed resident profile after a successful completion, so the moved
  // household / new active lease render immediately on the Lease Data tab (ENG-30151 Sc8/Sc13).
  onCompleteSuccess?: () => void,
};

// Section subheader — icon + 16px non-bold title (DS `bodyLarge`), matching the sibling cards.
const SectionTitle = ({ icon, message }) => (
  <Stack direction={'row'} spacing={1} alignItems={'center'}>
    {icon}
    <Typography variant={'bodyLarge'}>
      <FormattedMessage {...message} />
    </Typography>
  </Stack>
);

// A read-only detail line: 14px, non-bold label (DS `bodyMedium`) + value/link.
const DetailRow = ({ label, children }) => (
  <Typography variant={'bodyMedium'}>
    <FormattedMessage {...label} />: <span>{children}</span>
  </Typography>
);

const ResidentTransferCard = ({
  organizationId,
  propertyId,
  householdId,
  enabled,
  transferReasonOptions,
  onTransferChanged,
  onCancelSuccess,
  onCompleteSuccess,
}: Props) => {
  const [expanded, setExpanded] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  // ENG-30631: the autosave round-trip leaves a gap where the box still shows its old state, so a
  // second click would fire a contradictory update. Track every key currently saving — toggles run
  // concurrently, so a single pending key would release the earlier box as soon as the next starts.
  const [pendingActionKeys, setPendingActionKeys] = useState(() => new Set());
  const intl = useIntl();
  const { data: transfer } = useActiveTransfer(
    organizationId,
    propertyId,
    householdId,
    enabled,
  );
  const { mutateAsync: updateActions } = useUpdateTransferActions({
    organizationId,
    propertyId,
    householdId,
  });
  const cancelMutation = useCancelActiveTransfer({
    organizationId,
    propertyId,
    householdId,
  });
  const completeMutation = useCompleteActiveTransfer({
    organizationId,
    propertyId,
    householdId,
    onCompleteSuccess,
  });
  const editMutation = useEditActiveTransfer({
    organizationId,
    propertyId,
    householdId,
  });

  // Permission gating mirrors the Snapshot Start Transfer pattern: the card stays visible (read
  // access is `resident-read`, not this permission), but every WRITE action — Cancel and the
  // Transfer Actions checkboxes (both backed by the `lease-transfer-start`-scoped routes) — is
  // disabled without the transfer permission, with a tooltip explaining why (ENG-30150 Sc3,
  // ENG-30824).
  const canModifyTransfer = useHasPermission('lease-transfer-start');
  const cancelTooltipText = !canModifyTransfer
    ? intl.formatMessage(messages.cancelTransferNoPermission)
    : '';

  // Editing an active transfer requires the same transfer permission as the other write actions, so
  // reuse the existing boolean instead of re-checking the permission (which would risk the two
  // drifting). Without it the Edit link is shown disabled with an explanatory tooltip.
  const editTooltipText = !canModifyTransfer
    ? intl.formatMessage(messages.editNoPermission)
    : '';

  if (!enabled || !transfer) return null;

  const { details, requirements, actions, completeEnabled, dateEligible } =
    transfer;

  // Final Complete-button gating (Sc1–Sc4, Sc18–Sc20): requirements + actions (completeEnabled),
  // the Transfer-Date gate (dateEligible), permission, and in-flight state.
  const completeDisabled =
    !completeEnabled ||
    !dateEligible ||
    !canModifyTransfer ||
    completeMutation.isLoading;

  // ENG-31236: Cancel Transfer and Complete Transfer are always both on screen here (they disable
  // rather than hide), so the pair is always laid out as equal flex children.
  const { stackSx, tooltipContainerSx, buttonWrapperSx, fullWidth } =
    buildTransferActionLayout(true);

  // Tooltip precedence (empty string = no tooltip): no-permission wins, then future-dated; a
  // pending-requirement/action disable shows no tooltip (matches ENG-30149).
  let completeTooltipText = '';
  if (!canModifyTransfer) {
    completeTooltipText = intl.formatMessage(
      messages.completeTransferNoPermission,
    );
  } else if (!dateEligible) {
    completeTooltipText = intl.formatMessage(
      messages.completeTransferFutureDate,
      { transferDate: formatTransferDate(details.transferDate) },
    );
  }

  // ENG-31072: a conventional household's transfer creates no certifications, so the Cancel and
  // Edit notifications must drop the compliance-review wording. Undefined (older payload) keeps
  // the fuller wording.
  const hasTransferCertifications = details.hasTransferCertifications !== false;

  // Send only the fields that actually changed: a reason-only edit posts { transferReasonId }
  // (Sc7) and a date-only edit posts { transferDate } (Sc10).
  const changedTransferFields = (values) => {
    const payload = {};
    if (values.transferReasonId !== details.transferReasonId) {
      payload.transferReasonId = values.transferReasonId;
    }
    if (values.transferDate !== details.transferDate) {
      payload.transferDate = values.transferDate;
    }
    return payload;
  };

  // The card owns the edit mutation and modal lifecycle: close only on success; on failure keep
  // the modal open and surface the error via a toast (mirrors the Snapshot save error handling).
  // If nothing changed, close without calling the edit mutation.
  const handleEditSave = (values) => {
    const payload = changedTransferFields(values);
    if (Object.keys(payload).length === 0) {
      setEditModalOpen(false);
      return Promise.resolve();
    }
    return editMutation
      .mutateAsync(payload)
      .then(() => {
        setEditModalOpen(false);
        // A date change resets the transfer lease (dates/execution/generated doc) on the
        // redux-backed Lease Data tab, which only refreshes by reloading the resident.
        if (payload.transferDate !== undefined) {
          onTransferChanged?.();
        }
      })
      .catch((error) =>
        toastr.error('Error', error?.message || error?.networkError || error),
      );
  };

  return (
    <Paper>
      <Box padding={2}>
        <Stack spacing={2}>
          {/* Card header: title (no icon) + collapse control. */}
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography variant={'titleLarge'}>
              <FormattedMessage {...messages.cardTitle} />
            </Typography>
            <IconButton
              size={'small'}
              aria-label={'toggle-resident-transfer'}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          {/* Transfer Details: Assignment icon + title on the left, secondary Edit button on the right. */}
          <Stack spacing={1}>
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <SectionTitle
                icon={<AssignmentIcon />}
                message={messages.detailsTitle}
              />
              {/* Gated Edit button: CL secondary button with a pencil icon. Disabled with a
                  tooltip (wrapped in a span so the tooltip can fire) when the user lacks
                  permission. Available whenever the card renders, independent of requirement/action
                  state. */}
              <Tooltip
                title={editTooltipText}
                placement={'top'}
                variant={'light'}
              >
                <span>
                  <Button
                    variant={'text'}
                    startIcon={<PencilIcon fontSize={'small'} />}
                    disabled={!canModifyTransfer}
                    onClick={
                      canModifyTransfer
                        ? () => setEditModalOpen(true)
                        : undefined
                    }
                  >
                    <FormattedMessage {...messages.editDetails} />
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            <DetailRow label={messages.unit}>
              {details.unit ? (
                <ReactRouterLink
                  to={getUrlWithSelectedPropertyId(`/unit/${details.unitId}`)}
                >
                  {details.unit}
                </ReactRouterLink>
              ) : null}
            </DetailRow>
            <DetailRow label={messages.floorPlan}>
              {details.floorPlan ? (
                <ReactRouterLink
                  to={getUrlWithSelectedPropertyId(
                    `/floorplan/${details.floorPlanId}`,
                  )}
                >
                  {details.floorPlan}
                </ReactRouterLink>
              ) : null}
            </DetailRow>
            <DetailRow label={messages.reason}>
              {details.reasonForTransfer}
            </DetailRow>
            <DetailRow label={messages.transferDate}>
              {formatTransferDate(details.transferDate)}
            </DetailRow>
          </Stack>
        </Stack>

        {/* Collapsible region: Requirements + Actions + button row hide when collapsed. */}
        {expanded && (
          <Stack spacing={2} sx={{ marginTop: 2 }}>
            <Divider sx={{ marginY: 1 }} />
            <Stack spacing={1}>
              <SectionTitle
                icon={<ContractIcon />}
                message={messages.requirementsTitle}
              />
              {requirements.map((r) => (
                <Stack
                  key={r.key}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography variant={'bodyMedium'}>{r.label}</Typography>
                  <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    <Typography variant={'bodySmall'}>
                      <FormattedMessage
                        {...(r.status === 'COMPLETE'
                          ? messages.statusComplete
                          : messages.statusPending)}
                      />
                    </Typography>
                    <Box
                      component={'span'}
                      sx={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor:
                          r.status === 'COMPLETE' ? green.main : yellow.main,
                      }}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ marginY: 1 }} />
            <Stack spacing={1}>
              <SectionTitle
                icon={<ChecklistIcon />}
                message={messages.actionsTitle}
              />
              {ACTION_KEYS.map((key) => (
                // Label left, CL Checkbox right-aligned (mockup).
                <Stack
                  key={key}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography variant={'bodyMedium'}>
                    <FormattedMessage {...messages[key]} />
                  </Typography>
                  <Checkbox
                    inputProps={{
                      'aria-label': intl.formatMessage(messages[key]),
                    }}
                    checked={actions[key] === true}
                    disabled={!canModifyTransfer || pendingActionKeys.has(key)}
                    onChange={async (e, checked) => {
                      setPendingActionKeys((keys) => new Set(keys).add(key));
                      try {
                        await updateActions({ [key]: checked });
                      } catch (error) {
                        // Autosave failures already surface through the card's refetched state;
                        // swallowing here keeps the pre-ENG-30631 behavior.
                      } finally {
                        setPendingActionKeys((keys) => {
                          const remaining = new Set(keys);
                          remaining.delete(key);
                          return remaining;
                        });
                      }
                    }}
                  />
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ marginY: 1 }} />
            {/* Button row: gated Cancel Transfer (ENG-30150) + gated Complete Transfer. */}
            <Stack
              direction={'row'}
              spacing={TRANSFER_ACTION_ROW_SPACING}
              justifyContent={'center'}
              sx={stackSx}
            >
              <Tooltip
                title={cancelTooltipText}
                placement={'top'}
                variant={'light'}
                containerSX={tooltipContainerSx}
              >
                {/* span wrapper: a disabled MUI button swallows pointer events, so the Tooltip
                    needs a non-disabled wrapper to fire on hover/focus. It also fills the Tooltip's
                    flex container so this button matches Complete Transfer's width (ENG-31236). */}
                <Box component={'span'} sx={buttonWrapperSx}>
                  <Button
                    variant={'primarySubtle'}
                    type={'button'}
                    fullWidth={fullWidth}
                    disabled={!canModifyTransfer || cancelMutation.isLoading}
                    onClick={() => setCancelModalOpen(true)}
                  >
                    <FormattedMessage {...messages.cancelTransfer} />
                  </Button>
                </Box>
              </Tooltip>
              <Tooltip
                title={completeTooltipText}
                placement={'top'}
                variant={'light'}
                containerSX={tooltipContainerSx}
              >
                <Box component={'span'} sx={buttonWrapperSx}>
                  <Button
                    variant={'contained'}
                    fullWidth={fullWidth}
                    disabled={completeDisabled}
                    onClick={() => setCompleteModalOpen(true)}
                  >
                    <FormattedMessage {...messages.completeTransfer} />
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </Box>
      <CancelTransferModal
        open={cancelModalOpen}
        hasTransferCertifications={hasTransferCertifications}
        onClose={() => setCancelModalOpen(false)}
        isConfirming={cancelMutation.isLoading}
        onConfirm={() =>
          cancelMutation.mutate(undefined, {
            onSuccess: () => {
              setCancelModalOpen(false);
              // Success banner (ENG-30823): the transfer + its lease are gone.
              toastr.success(
                intl.formatMessage(messages.cancelTransferSuccessTitle),
                intl.formatMessage(messages.cancelTransferSuccessMessage),
              );
              // Refresh the redux-backed profile so the Lease Data tab drops the removed
              // transfer lease instead of showing stale transfer data (ENG-30823).
              onCancelSuccess && onCancelSuccess();
            },
            onError: (error) => {
              toastr.error(
                intl.formatMessage(messages.cancelTransferErrorTitle),
                error?.message || error?.toString?.() || String(error),
              );
            },
          })
        }
      />
      <CompleteTransferModal
        open={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        isConfirming={completeMutation.isLoading}
        unit={details.unit}
        transferDate={formatTransferDate(details.transferDate)}
        onConfirm={() =>
          completeMutation.mutate(undefined, {
            onSuccess: () => {
              setCompleteModalOpen(false);
              toastr.success(
                intl.formatMessage(messages.completeTransferSuccessTitle),
                intl.formatMessage(messages.completeTransferSuccessMessage),
              );
            },
            onError: (error) => {
              // On a 409 confirm-time revalidation failure the card re-renders with the refreshed
              // requirement statuses (hook re-invalidates). Close the modal and surface the error.
              setCompleteModalOpen(false);
              toastr.error(
                intl.formatMessage(messages.completeTransferErrorTitle),
                error?.message || error?.toString?.() || String(error),
              );
            },
          })
        }
      />
      <StartTransferModal
        mode={'edit'}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        organizationId={organizationId}
        propertyId={propertyId}
        householdId={householdId}
        initialValues={{
          transferReasonId: details.transferReasonId,
          transferDate: details.transferDate,
        }}
        transferReasonOptions={transferReasonOptions}
        hasTransferCertifications={hasTransferCertifications}
        isSaving={editMutation.isLoading}
        onSave={handleEditSave}
        // ENG-30316: floor the Transfer Date at the day after the assigned unit's prior move-out.
        // Null when the unit has no prior occupancy, which leaves the picker unrestricted.
        minTransferDate={details.transferUnitEarliestTransferDate}
      />
    </Paper>
  );
};

export default ResidentTransferCard;
